/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Warren Dowey
 */

import { Injectable } from '@angular/core';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { ApiParamsModel } from '@app/models/api-params.model';
import { ProjectDataListService } from '@app/services/api-services/project-data/project-data-list.service';
import { ProjectDataModel } from '@app/models/project-data.model';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { ApiFilterModel } from '@app/models/api-filter.model';

@Injectable({
  providedIn: 'root',
})
export class ProjectDataService extends ApiBaseService {
  private _projectData: ProjectDataModel;
  protected _endpoint = 'project_data';

  constructor(
    http: HttpClient,
    authUserService: AuthUserService,
    private projectDataListService: ProjectDataListService
  ) {
    super(http, authUserService);
  }

  get projectData(): ProjectDataModel {
    return this._projectData;
  }

  set projectData(value: ProjectDataModel) {
    this._projectData = value;
  }

  /**
   *   Ryan Duffy
   *
   *  getProjectDataFromListByProjectId
   *
   * : Get projects-list data based on the if by filtering the API query
   *
   * @param id of type string
   *
   */
  public getProjectdataFromListByProjectId(projectId: string): void {
    const projectDataList = this.projectDataListService.getProjectDataById(projectId);
    let currentFetchTimestamp: number;
    let lastListFetch: number;
    let timeDifferenceFetches: number;
    if (projectDataList.length) {
      currentFetchTimestamp = new Date().getTime();
      lastListFetch = this.projectDataListService.fetchTimestamp
        ? this.projectDataListService.fetchTimestamp.getTime()
        : null;
      // Get the difference between the current time and the last time the list was fully fetched
      // This can be compared to see if we need to pull from the API and list rather than just one.
      timeDifferenceFetches = lastListFetch ? (currentFetchTimestamp - lastListFetch) / 1000 : null;
      // This subscription is used to ensure that the dataSubject is returned after the first event loop
      // This allows the resolvers to be completed rather than bugs occurring without the delay being implemented
      this.delaySubscription = of(projectDataList)
        .pipe(delay(0))
        .subscribe((projectData) => {
          // If the time difference betweem the current time and the last fetch is more than ten seconds
          // Pass the local list, also pass the notes from the API, also get the newest full list from the API
          if (timeDifferenceFetches && timeDifferenceFetches > 10) {
            this.dataSubject.next(projectData);
            this.getProjectDataById(projectId);
            this.projectDataListService.getData({});
          } else {
            this.dataSubject.next(projectData);
          }
          if (this.delaySubscription) {
            this.delaySubscription.unsubscribe();
          }
        });
      // If there is no local list, get the correct list from the API and also get the newest full list from the API
    } else {
      this.getProjectDataById(projectId);
      this.projectDataListService.getData({});
    }
  }

  /**
   *  Warren Dowey
   *
   *  getProjectDataById
   *
   * : Get a projectData based on the projectId by filtering the API query
   *
   * @param projectId of type string
   *
   * @returns Void
   */
  getProjectDataById(projectId: string): void {
    const apiParamsModel = new ApiParamsModel();
    apiParamsModel.filter = [{ field: 'project_id', operator: 'eq', value: projectId }];
    this.getData(apiParamsModel);
  }
  /**
   *  Ryan Duffy
   *
   *  getProjectDataByProjectId
   *
   * : Get a projectData based on the projectId by filtering the API query
   *
   * @param projectId of type string
   *
   * @returns Void
   */
  getProjectDataByProjectId(projectId: string): void {
    const apiParamsModel = new ApiParamsModel();
    apiParamsModel.filter = [{ field: 'project_id', operator: 'eq', value: projectId }];
    this.getData(apiParamsModel);
  }
  /**
   *  Ryan Duffy
   *
   *  getProjectDataByProjectId
   *
   * : Get a projectData based on the projectId by filtering the API query
   *
   * @param projectId of type string
   *
   * @returns Void
   */
  updateActiveProjecdatatByProjectId(projectId: string, updateData: Object): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel = new ApiFilterModel();
    apiFilterModel.field = ['project_id', 'is_active'];
    apiFilterModel.value = [projectId, true];
    apiFilterModel.operator = ['eq', 'eq'];
    apiFilterModel.joinOperator = 'AND';
    apiParamsModel.filter = [apiFilterModel];
    this.updateData(apiParamsModel, updateData);
  }

  /**
   *
   *  updateActiveProjecdatatInListByProjectId
   *
   *  Get a projectData based on the projectId by filtering the API query
   *
   * @param projectId
   * @param projectData
   */
  updateDataCacheByActiveProjectDatatAndProjectId(
    projectId: string,
    projectData: ProjectDataModel
  ): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel = new ApiFilterModel();
    this.projectDataListService.updateInList(projectData);
    apiFilterModel.field = ['project_id', 'is_active'];
    apiFilterModel.value = [projectId, true];
    apiFilterModel.operator = ['eq', 'eq'];
    apiFilterModel.joinOperator = 'AND';
    apiParamsModel.filter = [apiFilterModel];
    this.updateData(apiParamsModel, { changes: projectData.toJson()['changes'] });
  }
  /**
   * Warren Dowey
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type ProjectDataModel
   *
   * @returns void
   */
  protected handleData(data: ProjectDataModel[]): void {
    this.projectData = data[0];
    super.handleData(data);
  }
  /**
   *  Warren Dowey
   *
   *  handleInsert
   *
   * : handles the insert of a new projectData into the list
   *
   * @param data of type ProjectDataModel
   *
   * @returns void
   */
  protected handleInsert(data: ProjectDataModel[]): void {
    if (data && data.length) {
      this.projectData = new ProjectDataModel();
      this.projectData = this.projectData.fromJson(data[0]);
      this.projectDataListService.addToList(this.projectData);
    }
    super.handleInsert(this.projectData);
  }
  /**
   *  Warren Dowey
   *
   *  handleUpdate
   *
   * : handles the update of a projectData in the list
   *
   * @param data of type ProjectDataModel
   *
   * @returns void
   */
  protected handleUpdate(data: ProjectDataModel[]): void {
    if (data && data.length) {
      this.projectData = new ProjectDataModel();
      this.projectData = this.projectData.fromJson(data[0]);
      this.projectDataListService.updateInList(this.projectData);
    }
    super.handleUpdate(this.projectData);
  }
  /**
   *  Warren Dowey
   *
   *  handleDelete
   *
   * : deletes an projectData from the list
   *
   * @param data of type projectDataModel
   *
   * @returns void
   */
  protected handleDelete(data: ProjectDataModel[]): void {
    if (data && data.length) {
      this.projectData = new ProjectDataModel();
      this.projectData = this.projectData.fromJson(data[0]);
      this.deleteSubject.next(this.projectData);
    }
  }
}
